import { forwardRef } from "react";

/**
 * OverflowPanel - the "···" menu panel
 */
export const OverflowPanel = forwardRef(
  (
    {
      pos,
      hasTool,
      isOverflow,
      editor,
      onUploadImage,
      handleImageFiles,
      onUploadVideo,
      handleVideoFiles,
      setOverflowOpen,
      onTableInsert,
    },
    ref,
  ) => {
    if (!pos) return null;

    return (
      <div
        ref={ref}
        className="rte-overflow-panel"
        style={{ top: pos.top, left: pos.left }}
      >
        {/* Headings — shown here when size=medium or size=small */}
        {hasTool("headings") && isOverflow("headings") && (
          <>
            <button
              type="button"
              title="Heading 1"
              className={`rte-btn${editor?.isActive("heading", { level: 1 }) ? " active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().toggleHeading({ level: 1 }).run();
              }}
            >
              Title
            </button>
            <button
              type="button"
              title="Heading 2"
              className={`rte-btn${editor?.isActive("heading", { level: 2 }) ? " active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().toggleHeading({ level: 2 }).run();
              }}
            >
              Subtitle
            </button>
            <button
              type="button"
              title="Heading 3"
              className={`rte-btn${editor?.isActive("heading", { level: 3 }) ? " active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().toggleHeading({ level: 3 }).run();
              }}
            >
              Normal
            </button>
          </>
        )}

        {/* Alignment — shown here when size=medium or size=small */}
        {hasTool("align") && isOverflow("align") && (
          <>
            <button
              type="button"
              title="Align left"
              className={`rte-btn${
                editor?.getAttributes("paragraph")?.textAlign === "left" ||
                !editor?.getAttributes("paragraph")?.textAlign
                  ? " active"
                  : ""
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().setTextAlign("left").run();
              }}
            >
              <span className="material-icons">format_align_left</span>
            </button>
            <button
              type="button"
              title="Align center"
              className={`rte-btn${
                editor?.getAttributes("paragraph")?.textAlign === "center"
                  ? " active"
                  : ""
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().setTextAlign("center").run();
              }}
            >
              <span className="material-icons">format_align_center</span>
            </button>
            <button
              type="button"
              title="Align right"
              className={`rte-btn${
                editor?.getAttributes("paragraph")?.textAlign === "right"
                  ? " active"
                  : ""
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().setTextAlign("right").run();
              }}
            >
              <span className="material-icons">format_align_right</span>
            </button>
          </>
        )}

        {/* Lists — shown here when size=small */}
        {hasTool("lists") && isOverflow("lists") && (
          <>
            <button
              type="button"
              title="Bullet list"
              className={`rte-btn${editor?.isActive("bulletList") ? " active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().toggleBulletList().run();
              }}
            >
              <span className="material-icons">format_list_bulleted</span>
            </button>
            <button
              type="button"
              title="Ordered list"
              className={`rte-btn${editor?.isActive("orderedList") ? " active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().toggleOrderedList().run();
              }}
            >
              <span className="material-icons">format_list_numbered</span>
            </button>
          </>
        )}

        {/* Image — shown here when size=medium or size=small */}
        {hasTool("image") && !!onUploadImage && isOverflow("image") && (
          <button
            type="button"
            title="Insert image"
            className="rte-btn"
            onClick={async (e) => {
              e.preventDefault();
              setOverflowOpen(false);
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "image/*";
              input.multiple = false;
              input.onchange = async (ev) => {
                const file = ev.target.files[0];
                if (file) await handleImageFiles([file]);
              };
              input.click();
            }}
          >
            <span className="material-icons">image</span>
          </button>
        )}

        {/* Video — shown here when size=medium or size=small */}
        {hasTool("video") && !!onUploadVideo && isOverflow("video") && (
          <button
            type="button"
            title="Insert video"
            className="rte-btn"
            onClick={async (e) => {
              e.preventDefault();
              setOverflowOpen(false);
              const input = document.createElement("input");
              input.type = "file";
              input.accept = "video/*";
              input.multiple = false;
              input.onchange = async (ev) => {
                const file = ev.target.files[0];
                if (file) await handleVideoFiles([file]);
              };
              input.click();
            }}
          >
            <span className="material-icons">videocam</span>
          </button>
        )}

        {/* Table insert — shown here when size=medium or size=small */}
        {hasTool("table") && isOverflow("table") && (
          <button
            type="button"
            title="Insert table"
            className="rte-btn"
            onMouseDown={(e) => {
              e.preventDefault();
              onTableInsert();
              setOverflowOpen(false);
            }}
          >
            <span className="material-icons">table_chart</span>
          </button>
        )}

        {/* Base overflow tools — always in the ··· menu regardless of size */}
        {hasTool("strike") && isOverflow("strike") && (
          <button
            type="button"
            title="Strikethrough"
            className={`rte-btn${editor?.isActive("strike") ? " active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleStrike().run();
            }}
          >
            <s>S</s>
          </button>
        )}
        {hasTool("blockquote") && isOverflow("blockquote") && (
          <button
            type="button"
            title="Blockquote"
            className={`rte-btn${editor?.isActive("blockquote") ? " active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleBlockquote().run();
            }}
          >
            <span className="material-icons">format_quote</span>
          </button>
        )}
        {hasTool("code") && isOverflow("code") && (
          <button
            type="button"
            title="Inline code"
            className={`rte-btn${editor?.isActive("code") ? " active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleCode().run();
            }}
          >
            <span className="material-icons">code</span>
          </button>
        )}
        {hasTool("codeblock") && isOverflow("codeblock") && (
          <button
            type="button"
            title="Code block"
            className={`rte-btn${editor?.isActive("codeBlock") ? " active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().toggleCodeBlock().run();
            }}
          >
            <span className="material-icons">data_object</span>
          </button>
        )}
        {hasTool("horizontalrule") && isOverflow("horizontalrule") && (
          <button
            type="button"
            title="Horizontal rule"
            className="rte-btn"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().setHorizontalRule().run();
            }}
          >
            <span className="material-icons">horizontal_rule</span>
          </button>
        )}
      </div>
    );
  },
);

OverflowPanel.displayName = "OverflowPanel";
